import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stopImpersonating, signOut } from '../../utils/authUtils';
import { showErrorToastFullScreen } from '../../utils/toastUtils';

import css from './ImpersonationBanner.module.scss';

/**
 * The standing reminder that this session is not the admin's own, and the
 * control that ends it.
 *
 * It exists because impersonation had no exit. `impersonatedUsername` was
 * written on the way in and removed only by `signOut` or a 401, and nothing on
 * screen said an impersonation was running at all — so an admin who wanted a
 * second user had to sign out and back in, and an admin who forgot they were
 * impersonating had no way to find out.
 *
 * PINNED TO THE BOTTOM, not the top, and that is structural rather than taste.
 * The desktop chrome is a fixed grid whose `main.main-content` is
 * `position: fixed; top: 20rem; height: calc(100vh - 20rem)` (App.scss), and
 * the mobile shell is a fixed two-row header the content is padded to clear.
 * A top band would have to move both of those constants — a change to desktop
 * rendering, which CLAUDE.md pins as the reference. The bottom edge is unclaimed
 * in both layouts, so the bar overlays nothing; `body.impersonating` in App.scss
 * adds the matching padding so it covers no content either.
 *
 * Rendered by `AppWrapper`, above the routes, so it reaches the console and the
 * user's app alike from one mount — the two branches of the router have
 * separate chrome (`AdminShell` vs. the inline layout in `App.tsx`), and
 * hanging it off either one would have left the other without it.
 */
const ImpersonationBanner: React.FC = () => {
  const navigate = useNavigate();
  // Read on every render rather than held in state. Impersonation begins with a
  // full page reload, so there is no in-session transition into this that a
  // subscription would need to catch, and the one transition OUT of it is the
  // button below, which navigates hard.
  const impersonatedUsername = localStorage.getItem('impersonatedUsername');

  // The bar is `position: fixed`, so it is out of flow and would sit ON the
  // last rows of whatever is scrolled to the bottom. The matching padding lives
  // in App.scss against this class rather than in the module, because the
  // element that needs it — `main.main-content` — is owned by two different
  // layouts and by neither of them exclusively. Cleaned up on unmount so a
  // session that ends the impersonation does not keep the gap.
  //
  // Declared ABOVE the early return: hooks cannot be conditional, so the guard
  // has to come after every one of them.
  useEffect(() => {
    if (!impersonatedUsername) return;
    document.body.classList.add('impersonating');
    return () => document.body.classList.remove('impersonating');
  }, [impersonatedUsername]);

  if (!impersonatedUsername) {
    return null;
  }

  const handleStop = () => {
    if (!stopImpersonating()) {
      // No admin token left to swap back to — the 401 interceptor clears it, so
      // this is the admin's own session having expired underneath them. There
      // is nothing to return to, and leaving them in the target's account would
      // be the worse half of that.
      //
      // Said out loud, through the app's own toast, before the sign-out
      // navigates away. Bouncing an admin to /signin with no account of why
      // reads as the button being broken — which is the one thing that is not
      // wrong here.
      showErrorToastFullScreen('Your admin session expired. Sign in again to continue.');
      signOut(navigate);
      return;
    }
    // A hard navigation, not `navigate()`: contexts above the router hold the
    // target's identity (AuthContext's token, the settings provider's payload),
    // and a client-side route change would carry every one of them into the
    // admin's session. Straight to the user list because that is what the admin
    // came back for.
    window.location.assign('/admin/users');
  };

  return (
    <div className={css.banner} role="status">
      <div className={css.identity}>
        <span className={css.pulse} aria-hidden="true" />
        <span className={css.eyebrow}>Impersonating</span>
        <p className={css.sentence}>
          You're viewing <strong className={css.username}>{impersonatedUsername}</strong>'s account.
        </p>
      </div>
      <button type="button" className={css.stop} onClick={handleStop}>
        Stop impersonating
      </button>
    </div>
  );
};

export default ImpersonationBanner;
